/**
 * The registers the Fleet console had and the ERP did not: two queues of people waiting, and the two
 * correction trails.
 *
 * Round claims and field reports are both OLDEST FIRST, unlike every other register in this ERP. Fleet's
 * comment explains why and it is right: *"this is a queue of people waiting, and answering the newest
 * request first is how the rider who asked at 4 a.m. is still waiting at noon."*
 */
import { CheckCircle2, Clock, Undo2, XCircle } from 'lucide-react';
import { Status, StatusBadge } from '../../ui/primitives';
import { asText } from '../operations/ActionDialog';
import { when, type OpsDescriptor, type OpsRow } from '../operations/types';

/**
 * How long somebody has been waiting, in hours.
 *
 * A Postgres interval reaches the browser as text, and WHICH text depends on the server's `IntervalStyle`:
 * `1 day 02:13:45` under the default, `P1DT2H13M45S` under `iso_8601`. Reading only one of the two would
 * make every wait on this screen read "just now" the day somebody changed a server setting — on the one
 * screen whose entire job is to show that people have been waiting. Both are read, and anything else
 * returns null rather than a confident zero.
 */
export function waitedHours(raw: unknown): number | null {
  if (!raw) return null;
  const text = String(raw);

  const iso = text.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:[\d.]+S)?)?$/);
  if (iso) return Number(iso[1] ?? 0) * 24 + Number(iso[2] ?? 0) + Number(iso[3] ?? 0) / 60;

  const postgres = text.match(/^(?:(\d+) days? ?)?(?:(-?\d+):(\d+):[\d.]+)?$/);
  if (postgres && (postgres[1] || postgres[2])) {
    return Number(postgres[1] ?? 0) * 24 + Number(postgres[2] ?? 0) + Number(postgres[3] ?? 0) / 60;
  }
  return null;
}

/** The badge, read the way a person would say it. */
function waited(row: OpsRow, key = 'waiting') {
  const raw = row[key];
  const hours = waitedHours(raw);
  // Not waiting (it has been answered) and unreadable are different, and neither is "just now".
  if (!raw) return null;
  if (hours === null) return <span className="text-muted" title={String(raw)}>—</span>;
  return (
    <StatusBadge tone={hours >= 4 ? 'refused' : hours >= 1 ? 'queued' : 'neutral'} icon={Clock} title={String(raw)}>
      {hours >= 24 ? `${Math.floor(hours / 24)}d` : hours >= 1 ? `${Math.floor(hours)}h` : 'just now'}
    </StatusBadge>
  );
}

export const PRE_CUTOVER: Record<string, OpsDescriptor> = {
  // ── Round claims ───────────────────────────────────────────────────────────────────────────────
  'round-claims': {
    id: 'round-claims', title: 'Round Claims', captures: false,
    subtitle:
      'Riders asking to run a round, and the supervisor answering — somebody is on leave, one rider is finishing another’s work, a vehicle has run out of fuel. **Oldest first**, because this is a queue of people waiting.',
    footer: 'Approving a claim records agreement. It staffs no round and moves no work: those are a trip assignment and a reassignment, each audited on its own',
    viewPermission: 'logistics.trips.view', view: 'round_claims',
    select: 'id,service_date,status,kind,reason,requested_at,decided_at,decision_note,route_code,route_name,rider_name,trip_code,waiting,created_at,updated_at,row_version',
    searchColumns: ['route_code', 'rider_name'],
    // The one register in this ERP sorted oldest first, and the subtitle says why.
    defaultSort: 'requested_at', defaultSortDirection: 'asc',
    filters: [
      { key: 'status', kind: 'select', label: 'Status', defaultValue: 'PENDING', options: [
        { value: 'PENDING', label: 'Waiting' }, { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Refused' }, { value: 'WITHDRAWN', label: 'Withdrawn' }] },
      { key: 'service_date', kind: 'dateRange', label: 'Round date' },
      { key: 'kind', kind: 'select', label: 'Why', options: [
        { value: 'COVER', label: 'Covering a round' }, { value: 'HELPING', label: 'Helping another rider' },
        { value: 'ON_LEAVE', label: 'Somebody is on leave' }, { value: 'VEHICLE_ISSUE', label: 'Vehicle problem' },
        { value: 'FUEL', label: 'Out of fuel' }, { value: 'OTHER', label: 'Something else' }] },
    ],
    columns: [
      { key: 'waiting', header: 'Waiting', width: '7rem', render: (r) => waited(r) },
      { key: 'rider_name', header: 'Rider', width: '14rem' },
      { key: 'route_code', header: 'Round', width: '7rem', mono: true },
      { key: 'service_date', header: 'For', width: '9rem' },
      { key: 'kind', header: 'Why', width: '12rem', render: (r) => String(r.kind).replace(/_/g, ' ').toLowerCase() },
      { key: 'status', header: 'Status', width: '9rem', render: (r) => <Status code={String(r.status)} /> },
      { key: 'reason', header: 'They said' },
      { key: 'trip_code', header: 'Round given', width: '10rem', mono: true, optional: true },
      { key: 'decision_note', header: 'Answer', optional: true },
      { key: 'decided_at', header: 'Answered', width: '11rem', optional: true, render: (r) => when(r.decided_at) },
    ],
    actions: [
      {
        key: 'approve', label: 'Approve', icon: CheckCircle2, permission: 'delivery.claims.decide',
        rpc: 'decide_round_claim', scope: 'row',
        available: (r) => r.status === 'PENDING',
        intro: <>This records that they may run it. <strong>It staffs nothing and moves no work</strong> — assign the round to them, and move any remaining stops, as separate acts.</>,
        fields: [
          { key: 'trip_id', label: 'The round, if it exists yet', kind: 'ref', view: 'trips', labelColumns: ['code', 'service_date'] },
          { key: 'note', label: 'Note', kind: 'text' },
        ],
        params: (v, r, key) => ({ p_claim_id: r?.id, p_decision: 'APPROVED', p_note: asText(v.note), p_trip_id: asText(v.trip_id), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Approved.'),
        invalidates: ['round-claims', 'trips'],
      },
      {
        key: 'reject', label: 'Refuse', icon: XCircle, permission: 'delivery.claims.decide',
        rpc: 'decide_round_claim', scope: 'row',
        available: (r) => r.status === 'PENDING',
        intro: <>Refusing somebody who asked for work needs a reason. They will see it.</>,
        fields: [{ key: 'note', label: 'Why', kind: 'textarea', required: true }],
        params: (v, r, key) => ({ p_claim_id: r?.id, p_decision: 'REJECTED', p_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Refused.'),
        invalidates: ['round-claims'],
      },
      {
        key: 'raise', label: 'Raise a claim', icon: Clock, permission: 'delivery.claims.decide',
        rpc: 'request_round_claim', scope: 'global',
        intro: <>For a rider who cannot ask themselves — they rang in, or their phone is dead.</>,
        fields: [
          { key: 'service_date', label: 'Round date', kind: 'date', required: true },
          { key: 'route_id', label: 'Round', kind: 'ref', required: true, view: 'routes', labelColumns: ['code', 'name'] },
          { key: 'rider_person_id', label: 'Rider', kind: 'ref', required: true, view: 'persons', labelColumns: ['display_name'] },
          { key: 'kind', label: 'Why', kind: 'select', options: [
            { value: 'COVER', label: 'Covering a round' }, { value: 'HELPING', label: 'Helping another rider' },
            { value: 'ON_LEAVE', label: 'Somebody is on leave' }, { value: 'VEHICLE_ISSUE', label: 'Vehicle problem' },
            { value: 'FUEL', label: 'Out of fuel' }, { value: 'OTHER', label: 'Something else' }] },
          { key: 'reason', label: 'Detail', kind: 'textarea' },
        ],
        params: (v, _r, key) => ({
          p_service_date: asText(v.service_date), p_route_id: asText(v.route_id),
          p_rider_person_id: asText(v.rider_person_id), p_kind: asText(v.kind) ?? 'COVER',
          p_reason: asText(v.reason), p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Asked.'),
        invalidates: ['round-claims'],
      },
    ],
    detail: { entityType: 'delivery.round_claims', title: (r) => `${String(r.rider_name)} · ${String(r.route_code)}`, evidence: false },
  },

  // ── Field reports ──────────────────────────────────────────────────────────────────────────────
  'field-reports': {
    id: 'field-reports', title: 'Field Reports', captures: false,
    subtitle:
      'What riders reported from the road: a broken bottle, a blocked gate, a bike that will not start. **Oldest first** — somebody is waiting on each of these.',
    footer: 'A report records what happened. It carries no consequence for anybody: the responsibility rules are an unconfirmed client proposal',
    viewPermission: 'delivery.view', view: 'field_reports',
    select: 'id,code,service_date,kind,status,note,reported_at,acknowledged_at,resolved_at,resolution_note,source_app,rider_name,trip_code,delivery_code,customer_code,customer_name,variant_code,position_recorded,waited,photo_count,created_at,updated_at,row_version',
    searchColumns: ['code', 'customer_code', 'customer_name', 'note'],
    defaultSort: 'reported_at', defaultSortDirection: 'asc',
    filters: [
      { key: 'status', kind: 'select', label: 'Status', defaultValue: 'OPEN', options: [
        { value: 'OPEN', label: 'Open' }, { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
        { value: 'RESOLVED', label: 'Resolved' }] },
      { key: 'service_date', kind: 'dateRange', label: 'Day' },
      { key: 'kind', kind: 'select', label: 'Kind', options: [
        { value: 'PRODUCT_DAMAGED', label: 'Product damaged' }, { value: 'PRODUCT_BROKEN', label: 'Product broken' },
        { value: 'PRODUCT_WRONG_VARIANT', label: 'Wrong variant' }, { value: 'PRODUCT_MISSING', label: 'Product missing' },
        { value: 'PRODUCT_QUALITY', label: 'Quality' }, { value: 'VARIANT_CHANGE_REQUEST', label: 'Wants a different variant' },
        { value: 'CUSTOMER_UNAVAILABLE', label: 'Customer unavailable' }, { value: 'ADDRESS_ISSUE', label: 'Address' },
        { value: 'DELIVERY_ISSUE', label: 'Delivery' }, { value: 'VEHICLE_ISSUE', label: 'Vehicle' },
        { value: 'HELP_REQUEST', label: 'Needs help' }, { value: 'OTHER', label: 'Something else' }] },
    ],
    columns: [
      { key: 'waited', header: 'Waiting', width: '7rem', render: (r) => waited(r, 'waited') },
      { key: 'code', header: 'Report', width: '10rem', mono: true },
      { key: 'kind', header: 'Kind', width: '14rem', render: (r) => String(r.kind).replace(/_/g, ' ').toLowerCase() },
      { key: 'status', header: 'Status', width: '10rem', render: (r) => <Status code={String(r.status)} /> },
      { key: 'rider_name', header: 'Rider', width: '12rem' },
      { key: 'customer_name', header: 'Customer', width: '14rem' },
      { key: 'note', header: 'They said' },
      { key: 'photo_count', header: 'Photos', width: '6rem', numeric: true,
        render: (r) => (Number(r.photo_count) > 0 ? String(r.photo_count) : <span className="text-muted">{'—'}</span>) },
      { key: 'service_date', header: 'Day', width: '9rem', optional: true },
      { key: 'trip_code', header: 'Round', width: '10rem', mono: true, optional: true },
      { key: 'source_app', header: 'Filed in', width: '9rem', optional: true },
      { key: 'resolution_note', header: 'How it was resolved', optional: true },
    ],
    actions: [
      {
        key: 'ack', label: 'Acknowledge', icon: Clock, permission: 'delivery.reports.manage',
        rpc: 'triage_field_report', scope: 'row',
        available: (r) => r.status === 'OPEN',
        intro: <>Tells the rider somebody has it. It does not close it.</>,
        fields: [{ key: 'note', label: 'Note', kind: 'text' }],
        params: (v, r, key) => ({ p_report_id: r?.id, p_status: 'ACKNOWLEDGED', p_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Acknowledged.'),
        invalidates: ['field-reports'],
      },
      {
        key: 'resolve', label: 'Resolve', icon: CheckCircle2, permission: 'delivery.reports.manage',
        rpc: 'triage_field_report', scope: 'row',
        available: (r) => r.status !== 'RESOLVED',
        intro: <>Say what was done. The rider who raised it can read this.</>,
        fields: [{ key: 'note', label: 'How it was resolved', kind: 'textarea', required: true }],
        params: (v, r, key) => ({ p_report_id: r?.id, p_status: 'RESOLVED', p_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Resolved.'),
        invalidates: ['field-reports'],
      },
    ],
    detail: {
      entityType: 'delivery.field_reports', title: (r) => `${String(r.code)} · ${String(r.kind).replace(/_/g, ' ').toLowerCase()}`,
      evidence: true,
    },
  },

  // ── Customer status changes ────────────────────────────────────────────────────────────────────
  'customer-status-changes': {
    id: 'customer-status-changes', title: 'Customer Status', captures: false,
    subtitle:
      'Changes to whether a customer is served. Apply one now, or gather several and publish them together — both are supported, and the history reads the same either way.',
    footer: 'A request changes nothing until it is published. The customer’s status is always the applied one, and there is one place to read it',
    viewPermission: 'customer.view', view: 'customer_status_changes',
    select: 'id,customer_id,customer_code,customer_name,previous_status,requested_status,current_status,effective_from,status,reason,channel,requested_at,applied_at,applied_in_batch,cancelled_at,cancel_reason,waiting,created_at,updated_at,row_version',
    searchColumns: ['customer_code', 'customer_name'],
    defaultSort: 'requested_at', defaultSortDirection: 'asc',
    filters: [
      { key: 'status', kind: 'select', label: 'Status', defaultValue: 'REQUESTED', options: [
        { value: 'REQUESTED', label: 'Waiting to publish' }, { value: 'APPLIED', label: 'Applied' },
        { value: 'CANCELLED', label: 'Cancelled' }] },
      { key: 'requested_status', kind: 'select', label: 'Changing to', options: [
        { value: 'ACTIVE', label: 'Active' }, { value: 'PAUSED', label: 'Paused' },
        { value: 'INACTIVE', label: 'Inactive' }, { value: 'RETIRED', label: 'Retired' }] },
      { key: 'channel', kind: 'select', label: 'Asked via', options: [
        { value: 'OFFICE', label: 'The office' }, { value: 'WHATSAPP', label: 'WhatsApp' },
        { value: 'INTEGRATION', label: 'Another system' }] },
    ],
    columns: [
      { key: 'waiting', header: 'Waiting', width: '7rem', render: (r) => waited(r) },
      { key: 'customer_name', header: 'Customer', width: '15rem' },
      { key: 'customer_code', header: 'Code', width: '11rem', mono: true },
      { key: 'previous_status', header: 'From', width: '8rem', render: (r) => <Status code={String(r.previous_status)} /> },
      { key: 'requested_status', header: 'To', width: '8rem', render: (r) => <Status code={String(r.requested_status)} /> },
      // What the customer IS, beside what was asked. One place to read the truth.
      { key: 'current_status', header: 'Is now', width: '8rem', render: (r) => <Status code={String(r.current_status)} /> },
      { key: 'status', header: 'Request', width: '11rem',
        render: (r) => (r.status === 'APPLIED'
          ? <StatusBadge tone="accepted" icon={CheckCircle2}>{r.applied_in_batch ? 'Published' : 'Applied'}</StatusBadge>
          : <Status code={String(r.status) === 'REQUESTED' ? 'PENDING' : String(r.status)} />) },
      { key: 'channel', header: 'Asked via', width: '9rem', render: (r) => String(r.channel).replace(/_/g, ' ').toLowerCase() },
      { key: 'reason', header: 'Why' },
      { key: 'applied_at', header: 'Applied', width: '11rem', optional: true, render: (r) => when(r.applied_at) },
      { key: 'cancel_reason', header: 'Cancelled because', optional: true },
    ],
    actions: [
      {
        key: 'request', label: 'Change a status', icon: Clock, permission: 'customer.edit',
        rpc: 'request_customer_status', scope: 'global',
        intro: <>Leave <strong>apply now</strong> off to gather it with others and publish them together. The customer does not change until it is applied either way.</>,
        fields: [
          { key: 'customer_id', label: 'Customer', kind: 'ref', required: true, view: 'customers', labelColumns: ['code', 'name'] },
          { key: 'status', label: 'To', kind: 'select', required: true, options: [
            { value: 'ACTIVE', label: 'Active — serve them' }, { value: 'PAUSED', label: 'Paused — stop for now' },
            { value: 'INACTIVE', label: 'Inactive' }, { value: 'RETIRED', label: 'Retired' }] },
          { key: 'effective_from', label: 'From', kind: 'date' },
          { key: 'channel', label: 'Asked via', kind: 'select', options: [
            { value: 'OFFICE', label: 'The office' }, { value: 'WHATSAPP', label: 'WhatsApp' },
            { value: 'INTEGRATION', label: 'Another system' }] },
          { key: 'apply_now', label: 'Apply now', kind: 'boolean', hint: 'Off gathers it for the next publish' },
          { key: 'reason', label: 'Why', kind: 'textarea' },
        ],
        params: (v, _r, key) => ({
          p_customer_id: asText(v.customer_id), p_status: asText(v.status),
          p_reason: asText(v.reason), p_effective_from: asText(v.effective_from),
          p_channel: asText(v.channel) ?? 'OFFICE', p_apply_now: v.apply_now === true,
          p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Recorded.'),
        invalidates: ['customer-status-changes', 'customers'],
      },
      {
        key: 'publish', label: 'Publish everything waiting', icon: CheckCircle2, permission: 'customer.status.publish',
        rpc: 'publish_customer_status_changes', scope: 'global',
        intro: <>Applies every waiting change in one go. Each customer is <strong>re-read first</strong>: one somebody has already changed is skipped with its reason rather than overwritten.</>,
        fields: [
          { key: 'confirm', label: 'Apply them', kind: 'boolean', hint: 'Leave off to see what would change' },
          { key: 'reason', label: 'Note', kind: 'text' },
        ],
        params: (v, _r, key) => ({ p_change_ids: null, p_confirm: v.confirm === true, p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Published.'),
        invalidates: ['customer-status-changes', 'customers'],
      },
      {
        key: 'cancel', label: 'Cancel this request', icon: Undo2, permission: 'customer.edit',
        rpc: 'cancel_customer_status_change', scope: 'row',
        available: (r) => r.status === 'REQUESTED',
        intro: <>It never took effect, so the customer is unchanged. The record stays.</>,
        fields: [{ key: 'reason', label: 'Why', kind: 'textarea', required: true }],
        params: (v, r, key) => ({ p_change_id: r?.id, p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Cancelled.'),
        invalidates: ['customer-status-changes'],
      },
    ],
    detail: { entityType: 'sales.customer_status_changes', title: (r) => `${String(r.customer_name)} · ${String(r.requested_status).toLowerCase()}`, evidence: false },
  },

  // ── Corrections ────────────────────────────────────────────────────────────────────────────────
  'delivery-assignments': {
    id: 'delivery-assignments', title: 'Delivery Moves', captures: false,
    subtitle:
      'Where one delivery was moved between rounds without moving its door — a variant carried by the rider who had it in their crate.',
    footer: 'Moving a delivery moves no stock: the round that carries it is not the round that drew it from the store',
    viewPermission: 'delivery.view', view: 'delivery_assignments',
    select: 'id,delivery_id,delivery_code,delivery_status,from_trip_id,from_trip_code,from_rider_name,to_trip_id,to_trip_code,to_rider_name,restores_default,reason,moved_at,service_date,variant_code,customer_code,customer_name,door_code,created_at,row_version',
    searchColumns: ['delivery_code', 'customer_code', 'customer_name'],
    defaultSort: 'moved_at', defaultSortDirection: 'desc',
    filters: [{ key: 'service_date', kind: 'dateRange', label: 'Service date' }],
    columns: [
      { key: 'moved_at', header: 'Moved', width: '11rem', render: (r) => when(r.moved_at) },
      { key: 'customer_name', header: 'Customer', width: '14rem' },
      { key: 'variant_code', header: 'Variant', width: '7rem', mono: true },
      { key: 'from_rider_name', header: 'From', width: '12rem' },
      { key: 'to_rider_name', header: 'To', width: '12rem' },
      { key: 'restores_default', header: 'Direction', width: '10rem',
        render: (r) => (r.restores_default
          ? <StatusBadge tone="neutral" icon={Undo2}>back to the door</StatusBadge>
          : <StatusBadge tone="info" icon={CheckCircle2}>moved away</StatusBadge>) },
      { key: 'reason', header: 'Why' },
      { key: 'delivery_code', header: 'Delivery', width: '10rem', mono: true, optional: true },
      { key: 'from_trip_code', header: 'From round', width: '9rem', mono: true, optional: true },
      { key: 'to_trip_code', header: 'To round', width: '9rem', mono: true, optional: true },
    ],
    actions: [],
    detail: { entityType: 'delivery.delivery_assignments', title: (r) => `Moved · ${String(r.delivery_code)}`, evidence: false },
  },

  'delivery-corrections': {
    id: 'delivery-corrections', title: 'Delivery Corrections', captures: false,
    subtitle:
      'Where a supervisor overruled what was recorded at a door, with what it was kept beside what it became.',
    footer: 'The stock ledger is NOT adjusted by a correction: what left the store is a separate fact from what somebody wrote down',
    viewPermission: 'delivery.view', view: 'delivery_corrections',
    select: 'id,delivery_id,delivery_code,previous_status,previous_units,new_status,new_units,reason_code,note,corrected_at,service_date,trip_code,customer_code,customer_name,variant_code,created_at,row_version',
    searchColumns: ['delivery_code', 'customer_code', 'customer_name'],
    defaultSort: 'corrected_at', defaultSortDirection: 'desc',
    filters: [{ key: 'service_date', kind: 'dateRange', label: 'Service date' }],
    columns: [
      { key: 'corrected_at', header: 'Corrected', width: '11rem', render: (r) => when(r.corrected_at) },
      { key: 'customer_name', header: 'Customer', width: '14rem' },
      { key: 'delivery_code', header: 'Delivery', width: '10rem', mono: true },
      { key: 'previous_status', header: 'Was', width: '9rem', render: (r) => <Status code={String(r.previous_status)} /> },
      { key: 'new_status', header: 'Became', width: '9rem', render: (r) => <Status code={String(r.new_status)} /> },
      { key: 'previous_units', header: 'Was', width: '5rem', numeric: true },
      { key: 'new_units', header: 'Now', width: '5rem', numeric: true },
      { key: 'reason_code', header: 'Reason', width: '14rem', render: (r) => String(r.reason_code).replace(/_/g, ' ').toLowerCase() },
      { key: 'note', header: 'What was said' },
    ],
    actions: [],
    detail: { entityType: 'delivery.delivery_corrections', title: (r) => `Correction · ${String(r.delivery_code)}`, evidence: false },
  },

  'custody-corrections': {
    id: 'custody-corrections', title: 'Custody Corrections', captures: false,
    subtitle: 'Where a container’s recorded whereabouts were corrected — a mis-scan, a missed scan, or one found at the dairy that the system believed a customer held.',
    footer: 'Each correction is itself a custody event, so the append-only trail stays the whole story. No historical event is altered',
    viewPermission: 'bottles.view', view: 'custody_corrections',
    select: 'id,bottle_id,bottle_code,previous_state,new_state,reason_code,note,corrected_at,previous_customer_code,new_customer_code,created_at,row_version',
    searchColumns: ['bottle_code', 'previous_customer_code', 'new_customer_code'],
    defaultSort: 'corrected_at', defaultSortDirection: 'desc',
    columns: [
      { key: 'corrected_at', header: 'Corrected', width: '11rem', render: (r) => when(r.corrected_at) },
      { key: 'bottle_code', header: 'Container', width: '12rem', mono: true },
      { key: 'previous_state', header: 'Was', width: '11rem', render: (r) => <Status code={String(r.previous_state)} /> },
      { key: 'new_state', header: 'Became', width: '11rem', render: (r) => <Status code={String(r.new_state)} /> },
      { key: 'previous_customer_code', header: 'Was with', width: '11rem', mono: true },
      { key: 'new_customer_code', header: 'Now with', width: '11rem', mono: true },
      { key: 'reason_code', header: 'Reason', width: '12rem', render: (r) => String(r.reason_code).replace(/_/g, ' ').toLowerCase() },
      { key: 'note', header: 'What was said' },
    ],
    actions: [],
    detail: { entityType: 'assets.custody_corrections', title: (r) => `Correction · ${String(r.bottle_code)}`, evidence: false },
  },
};
